import { MoreVerticalIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { formatDate } from '@/lib/format';
import type { PurchaseOrderComment } from '@/types/core/purchase-order/purchase-order-comment';

type PurchaseOrderMainCommentProps = {
  comment: PurchaseOrderComment;
  openEditDialog: (comment: PurchaseOrderComment) => void;
  openDeleteDialog: (comment: PurchaseOrderComment) => void;
};

export function PurchaseOrderMainComment({
  comment,
  openEditDialog,
  openDeleteDialog,
}: PurchaseOrderMainCommentProps) {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language;

  const onEditClick = () => {
    openEditDialog(comment);
  };

  const onDeleteClick = () => {
    openDeleteDialog(comment);
  };

  return (
    <div className='flex items-center gap-3 text-sm'>
      <div className='min-w-0 flex-1'>
        <HoverCard>
          <HoverCardTrigger asChild onClick={(e) => e.stopPropagation()}>
            <p className='cursor-pointer truncate hover:underline'>
              {comment.value}
            </p>
          </HoverCardTrigger>
          <HoverCardContent align='start' className='w-100 whitespace-pre-line'>
            {comment.value}
          </HoverCardContent>
        </HoverCard>
      </div>
      <span className='shrink-0 whitespace-nowrap text-muted-foreground'>
        {t('by')} {comment.userLogin}{' '}
        {formatDate(comment.createdAt, {
          locale,
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })}
      </span>
      <div className='shrink-0'>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className='rounded-md bg-transparent text-muted-foreground text-sm hover:bg-muted/60 hover:text-foreground'
              type='button'
            >
              <MoreVerticalIcon className='size-5' />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='start' className='w-48'>
            <DropdownMenuItem onClick={onEditClick}>
              {t('edit')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDeleteClick}>
              {t('delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
